// @flow
import * as React from 'react';
import {
    BarChart as RechartsBarChart,
    Bar as RechartsBar,
    CartesianGrid as RechartsCartesianGrid,
    Tooltip as RechartsTooltip,
    Legend as RechartsLegend
} from 'recharts';
import { themr, type ThemeType } from 'react-css-themr';
import classnames from 'classnames';
import moment from 'moment-timezone';
import uniqBy from 'lodash/uniqBy';
import NumberUtils from 'common/utils/NumberUtils';
import { Map } from 'immutable';
import Units from 'common/enums/UnitEnum';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';

import { GroupedDataTooltipWithSelectedState } from 'common/components/core/charts/_shared/GroupedDataTooltip';
import {
    createXAxis,
    createYAxis
} from 'common/components/core/charts/_shared/CustomAxis';
import { createBrush } from 'common/components/core/charts/_shared/CustomBrush';
import { renderReferenceLines } from './PjpChartReferenceLines';
import PjpChartsCustomLegend from './PjpChartsCustomLegend';

import { memoizedImmutable } from 'common/utils/Memoizer';
import { dashboardColors, DistinctColors } from 'common/enums/ColorEnum';

import type {
    PjpCartesianChartDataType,
    PjpDataKeyType,
    PjpDataItemType
} from 'common/entity/pjpMaster/pjpDataTypes/insights/PjpCartesianChartDataType';

import theme from './PjpChart.scss';

const [DEFAULT_COLOR] = DistinctColors;

type Props = {
    height: number,
    width: number,
    relativeHeight: number,
    relativeWidth: number,
    data: PjpCartesianChartDataType,
    alignLegend?: 'right',
    unit: string,
    selectedDataKeys: Array<string>,
    theme: ThemeType,
    unitConverter: UnitConversionHelper
};

type State = {
    startIndex?: ?number,
    endIndex?: ?number,
    activeIndex?: ?number,
    activeTooltipStyleTransform?: ?string
};

class PjpBarChart extends React.PureComponent<Props, State> {
    state: State = {};

    componentDidMount() {
        this.isAnimationActive = false;
    }

    @memoizedImmutable()
    _getChartUnit() {
        const { metricUnitType, unitConverter, unit } = this.props;
        let chartUnit;
        if (metricUnitType === 'DISTANCE') {
            chartUnit = unitConverter.getPreferredUnit({
                sourceUnit: Units.list.length.Meter
            });
            return {
                label: chartUnit.abbr,
                pluralLabel: chartUnit.abbr
            };
        } else if (metricUnitType === 'TIME') {
            chartUnit = unitConverter.getPreferredUnit({
                sourceUnit: Units.list.time.Second
            });
            return {
                label: chartUnit.abbr,
                pluralLabel: chartUnit.abbr
            };
        } else {
            return { label: unit, pluralLabel: '' };
        }
    }

    isAnimationActive: boolean = true;
    tooltipRef = React.createRef<RechartsTooltip>();

    @memoizedImmutable()
    _formatValues(data: PjpCartesianChartDataType) {
        const { unitConverter, metricUnitType, unit } = this.props;
        const { values, domainAxis } = data;
        return values.map((value, index) => {
            const domainValue = value[domainAxis.key];
            const valueMap = Map(value);
            let formattedUnit;
            const formattedSubValues = valueMap
                .map(singleValue => {
                    let formattedValue = Map();
                    if (metricUnitType === 'DISTANCE') {
                        const convertedValue = unitConverter.convertMeters({
                            value: singleValue,
                            pluralizeUnit: true,
                            roundOff: true,
                            decimals: 2,
                            showUnitAbbr: false,
                            padLeft: true
                        });
                        formattedValue = NumberUtils.toNumber(
                            convertedValue.value
                        );
                        formattedUnit = convertedValue.unit;
                    } else if (metricUnitType === 'TIME' && singleValue) {
                        const convertedValue = unitConverter.convertSeconds({
                            value: singleValue
                        });
                        formattedValue = convertedValue.value;
                        formattedUnit = convertedValue.unit;
                    } else {
                        formattedValue = singleValue;
                        formattedUnit = unit;
                    }
                    return formattedValue;
                })
                .toJS();
            return {
                ...formattedSubValues,
                unit: formattedUnit,
                index,
                [domainAxis.key]: this._formatDomainValue(
                    domainValue,
                    domainAxis.tickType
                )
            };
        });
    }

    @memoizedImmutable()
    _getLegendData(dataKeys: Array<PjpDataKeyType>) {
        const { metricUnitType, unit, unitConverter } = this.props;
        let convertedUnit;
        if (metricUnitType === 'DISTANCE') {
            convertedUnit = unitConverter.convertMeters({
                value: 0,
                pluralizeUnit: true,
                roundOff: true,
                decimals: 2,
                showUnitAbbr: false,
                padLeft: true
            }).unit;
        } else if (metricUnitType === 'TIME') {
            convertedUnit = unitConverter.convertSeconds({
                value: 0
            }).unit;
        } else {
            convertedUnit = { plural: unit };
        }
        return uniqBy(
            dataKeys.map((dataKey, index) => {
                return {
                    index,
                    name: dataKey.label,
                    color: dataKey.color || DEFAULT_COLOR,
                    unit: convertedUnit
                };
            }),
            item => `${item.name}-${item.color}`
        );
    }

    _filterDataKeys(
        dataKeys: Array<PjpDataKeyType>,
        selectedDataKeys: Array<string>
    ) {
        if (selectedDataKeys.length === 0) {
            return dataKeys;
        }
        return dataKeys.filter(dataKey => {
            return selectedDataKeys.indexOf(dataKey.key) > -1;
        });
    }

    _formatDomainValue(value: string | number, tickType: PjpDataItemType) {
        switch (tickType) {
            case 'STRING':
                return value;
            case 'DATE':
                return moment(value).format('DD MMM');
            case 'NUMBER':
                return value;
        }
        throw new Error(`Unknown TickType: ${tickType}`);
    }

    _formatTick = (value: number) => {
        const valueToRepresent = NumberUtils.formatNumber(value, true, 2);
        return valueToRepresent;
    };

    _handleBarClick = (areaObject: ?{ activeTooltipIndex: number }) => {
        if (!areaObject) {
            return;
        }
        const tooltipEl = this.tooltipRef.current;
        if (tooltipEl && tooltipEl.state.boxWidth) {
            const { transform } = tooltipEl.wrapperNode.style;
            this.setState({
                activeIndex: areaObject.activeTooltipIndex,
                activeTooltipStyleTransform: transform
            });
        }
    };

    _handleBrushChange = ({ startIndex, endIndex }) => {
        this.setState({ startIndex, endIndex });
    };

    _handleCloseTooltip = () => {
        this.setState({ activeIndex: null, activeTooltipStyleTransform: null });
    };

    _getPositionFromTransform = (transform?: ?string) => {
        const regex = /(\d+(.\d+)?)px/g;
        let m;
        const pos = [];
        if (transform) {
            while ((m = regex.exec(transform)) !== null) {
                // This is necessary to avoid infinite loops with zero-width matches
                if (m.index === regex.lastIndex) {
                    regex.lastIndex++;
                }

                pos.push(m[1]);
            }
        }
        return { x: parseInt(pos[0]), y: parseInt(pos[1]) };
    };

    _shouldShowBrush = filteredDataKeys => {
        const { width, data } = this.props;
        const noOfBarsPerGroup = filteredDataKeys.reduce(
            ({ count, stacksIdentified }, dataKey) => {
                if (!dataKey.stackId) {
                    return { count: count + 1, stacksIdentified };
                }
                if (stacksIdentified.indexOf(dataKey.stackId) === -1) {
                    return {
                        count: count + 1,
                        stacksIdentified: [...stacksIdentified, dataKey.stackId]
                    };
                }
                return { count, stacksIdentified };
            },
            { count: 0, stacksIdentified: [] }
        ).count;
        const noOfBarGroups = data.values.length;
        const noOfBars = noOfBarsPerGroup * noOfBarGroups;
        return width / noOfBars < 15;
    };

    render() {
        const {
            height,
            width,
            data,
            alignLegend,
            selectedDataKeys,
            theme,
            relativeHeight,
            relativeWidth
        } = this.props;
        const {
            startIndex,
            endIndex,
            activeIndex,
            activeTooltipStyleTransform
        } = this.state;
        const { domainAxis, rangeAxis, dataKeys, refLines } = data;

        const formattedValues = this._formatValues(data);

        const legendOnRight =
            (dataKeys.length > 4 && width / height > 4) ||
            alignLegend === 'right';

        const filteredDataKeys = this._filterDataKeys(
            dataKeys,
            selectedDataKeys
        );

        const showBrush = this._shouldShowBrush(filteredDataKeys);
        const forceShowTooltip = Number.isFinite(activeIndex);
        const chartUnit = this._getChartUnit();
        return (
            <RechartsBarChart
                className={classnames(theme.barChart, theme.chart, {
                    [theme[alignLegend]]: alignLegend,
                    [theme.right]: legendOnRight
                })}
                width={width}
                height={height}
                data={formattedValues}
                barGap={2}
                margin={showBrush ? { bottom: 20 } : {}}
                barCategoryGap={'12%'}
                onClick={forceShowTooltip ? undefined : this._handleBarClick}
            >
                {createXAxis({
                    domainAxis,
                    data,
                    startIndex,
                    endIndex,
                    chartType: 'BAR',
                    chartWidth: width,
                    relativeWidth,
                    relativeHeight,
                    theme,
                    unit: chartUnit
                })}

                {createYAxis({
                    rangeAxis,
                    data,
                    startIndex,
                    endIndex,
                    tickFormatter: this._formatTick,
                    filteredDataKeys,
                    relativeWidth,
                    relativeHeight,
                    theme,
                    unit: chartUnit
                })}

                <RechartsCartesianGrid
                    strokeDasharray="0 0"
                    stroke={dashboardColors.offWhite.code}
                    vertical={false}
                />

                <RechartsTooltip
                    ref={this.tooltipRef}
                    filterNull={false}
                    cursor={{ fillOpacity: 0.25 }}
                    forceShow={forceShowTooltip}
                    wrapperStyle={
                        forceShowTooltip
                            ? {
                                  visibility: 'visible',
                                  transform: activeTooltipStyleTransform
                              }
                            : undefined
                    }
                    position={
                        forceShowTooltip
                            ? this._getPositionFromTransform(
                                  activeTooltipStyleTransform
                              )
                            : ''
                    }
                    content={
                        <GroupedDataTooltipWithSelectedState
                            stacks={data.stacks}
                            dataKeys={filteredDataKeys}
                            unit={chartUnit}
                            onClose={this._handleCloseTooltip}
                            showTotal
                        />
                    }
                />

                {renderReferenceLines({ refLines })}

                <RechartsLegend
                    theme={theme}
                    width={legendOnRight ? Math.min(width / 5, 250) : undefined}
                    height={legendOnRight ? height : undefined}
                    align={legendOnRight ? 'right' : 'center'}
                    verticalAlign={legendOnRight ? 'middle' : 'bottom'}
                    layout={legendOnRight ? 'vertical' : 'horizontal'}
                    payload={this._getLegendData(filteredDataKeys)}
                    content={<PjpChartsCustomLegend unit={chartUnit} />}
                />

                {showBrush
                    ? createBrush({
                          chartType: 'BAR',
                          filteredDataKeys,
                          onBrushChange: this._handleBrushChange,
                          domainAxisKey: domainAxis.key,
                          formattedValues
                      })
                    : null}

                {filteredDataKeys.map(dataKey => {
                    return (
                        <RechartsBar
                            key={dataKey.key}
                            dataKey={dataKey.key}
                            name={dataKey.label}
                            fill={dataKey.color || DEFAULT_COLOR}
                            stackId={dataKey.stackId}
                            isAnimationActive={
                                formattedValues.length < 50 &&
                                this.isAnimationActive
                            }
                        />
                    );
                })}
            </RechartsBarChart>
        );
    }
}

export default themr('PjpBarChart', theme)(PjpBarChart);
